import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { notifications } from '@mantine/notifications';

import {
    DELETE_TRAINING_MATERIAL,
    UPDATE_TRAINING_MATERIAL,
} from '../apollo/mutations/training';
import {
    GET_TRAINING_CATEGORIES,
    GET_TRAINING_MATERIAL_DOWNLOAD_URL,
    GET_TRAINING_MATERIAL_VIEW_URL,
    GET_TRAINING_MATERIALS,
} from '../apollo/queries/training';
import { getErrorMessage } from '../helpers/getErrorMessage';
import { TrainingMaterial } from '../types/training';

type SaveEditValues = {
    id: number;
    title: string;
    description: string | null;
    categoryId: number;
};

type Props = {
    activeCategoryId: string | null;
    onActiveCategoryChange: (categoryId: string) => void;
};

export function useTrainingMaterialActions({
    activeCategoryId,
    onActiveCategoryChange,
}: Props) {
    const [viewerMaterial, setViewerMaterial] =
        useState<TrainingMaterial | null>(null);
    const [viewUrl, setViewUrl] = useState<string | null>(null);
    const [editTarget, setEditTarget] = useState<TrainingMaterial | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<TrainingMaterial | null>(
        null,
    );
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

    const [fetchViewUrl, { loading: viewLoading }] = useLazyQuery(
        GET_TRAINING_MATERIAL_VIEW_URL,
    );
    const [fetchDownloadUrl] = useLazyQuery(GET_TRAINING_MATERIAL_DOWNLOAD_URL);
    const [updateMaterial, { loading: updating }] = useMutation(
        UPDATE_TRAINING_MATERIAL,
        {
            refetchQueries: [
                { query: GET_TRAINING_MATERIALS },
                { query: GET_TRAINING_CATEGORIES },
            ],
        },
    );
    const [deleteMaterial, { loading: deleting }] = useMutation(
        DELETE_TRAINING_MATERIAL,
        {
            refetchQueries: [{ query: GET_TRAINING_MATERIALS }],
        },
    );

    const handleView = async (material: TrainingMaterial) => {
        setActionLoadingId(material.id);
        setViewerMaterial(material);
        setViewUrl(null);
        try {
            const { data, error } = await fetchViewUrl({
                variables: { id: material.id },
            });
            const url = data?.getTrainingMaterialViewUrl?.url;
            if (error || !url) {
                notifications.show({
                    title: 'Unable to open material',
                    message: error
                        ? getErrorMessage(error)
                        : 'Preview URL unavailable',
                    color: 'red',
                });
                setViewerMaterial(null);
                return;
            }
            setViewUrl(url);
        } catch (error) {
            notifications.show({
                title: 'Unable to open material',
                message: getErrorMessage(error),
                color: 'red',
            });
            setViewerMaterial(null);
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleDownload = async (material: TrainingMaterial) => {
        setActionLoadingId(material.id);
        try {
            const { data, error } = await fetchDownloadUrl({
                variables: { id: material.id },
            });
            const url = data?.getTrainingMaterialDownloadUrl?.url;
            if (error || !url) {
                notifications.show({
                    title: 'Download failed',
                    message: error
                        ? getErrorMessage(error)
                        : 'Download URL unavailable',
                    color: 'red',
                });
                return;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (error) {
            notifications.show({
                title: 'Download failed',
                message: getErrorMessage(error),
                color: 'red',
            });
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleViewMaterial = (material: TrainingMaterial) => {
        handleView(material).catch(() => undefined);
    };

    const handleDownloadMaterial = (material: TrainingMaterial) => {
        handleDownload(material).catch(() => undefined);
    };

    const handleDownloadViewerMaterial = () => {
        if (viewerMaterial) {
            handleDownload(viewerMaterial).catch(() => undefined);
        }
    };

    const handleCloseViewer = () => {
        setViewerMaterial(null);
        setViewUrl(null);
    };

    const handleCloseEdit = () => {
        setEditTarget(null);
    };

    const handleCloseDelete = () => {
        setDeleteTarget(null);
    };

    const handleSaveEdit = async (values: SaveEditValues) => {
        try {
            await updateMaterial({
                variables: {
                    input: values,
                },
            });
            notifications.show({
                title: 'Material updated',
                message: `"${values.title}" was saved`,
                color: 'teal',
            });
            if (
                activeCategoryId &&
                String(values.categoryId) !== activeCategoryId
            ) {
                onActiveCategoryChange(String(values.categoryId));
            }
            setEditTarget(null);
        } catch (error) {
            notifications.show({
                title: 'Update failed',
                message: getErrorMessage(error),
                color: 'red',
            });
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) {
            return;
        }
        try {
            await deleteMaterial({ variables: { id: deleteTarget.id } });
            notifications.show({
                title: 'Material deleted',
                message: `"${deleteTarget.title}" was removed`,
                color: 'teal',
            });
            setDeleteTarget(null);
        } catch (error) {
            notifications.show({
                title: 'Delete failed',
                message: getErrorMessage(error),
                color: 'red',
            });
        }
    };

    return {
        viewerMaterial,
        viewUrl,
        viewLoading,
        editTarget,
        deleteTarget,
        actionLoadingId,
        updating,
        deleting,
        setEditTarget,
        setDeleteTarget,
        handleViewMaterial,
        handleDownloadMaterial,
        handleDownloadViewerMaterial,
        handleCloseViewer,
        handleCloseEdit,
        handleCloseDelete,
        handleSaveEdit,
        handleDelete,
    };
}
